<template>
    <span
        ref="root"
        class="info-tooltip"
        :class="{
            'info-tooltip--preserve-trigger-color': preserveTriggerColor,
        }"
    >
        <button
            type="button"
            class="info-tooltip__trigger"
            :aria-label="text"
            :aria-expanded="String(isVisible)"
            :aria-describedby="isVisible ? tooltipId : undefined"
            @mouseenter="showTemporarily"
            @mouseleave="hideTemporarily"
            @focus="showTemporarily"
            @blur="hideTemporarily"
            @click="togglePinned"
        >
            <slot></slot>
        </button>
        <Teleport to="body">
            <span
                v-show="isVisible"
                :id="tooltipId"
                class="info-tooltip__content"
                role="tooltip"
                :style="tooltipPosition"
            >
                {{ text }}
            </span>
        </Teleport>
    </span>
</template>

<script>
let nextTooltipId = 0;

export default {
    name: 'InfoTooltip',
    props: {
        text: {
            type: String,
            required: true,
        },
        preserveTriggerColor: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        nextTooltipId += 1;

        return {
            isPinned: false,
            isVisible: false,
            tooltipId: `info-tooltip-${nextTooltipId}`,
            tooltipPosition: {
                left: '0px',
                top: '0px',
            },
        };
    },
    mounted() {
        document.addEventListener('click', this.handleOutsideClick);
        document.addEventListener('keydown', this.handleKeydown);
        window.addEventListener('resize', this.updatePosition);
        window.addEventListener('scroll', this.updatePosition, true);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleOutsideClick);
        document.removeEventListener('keydown', this.handleKeydown);
        window.removeEventListener('resize', this.updatePosition);
        window.removeEventListener('scroll', this.updatePosition, true);
    },
    methods: {
        showTemporarily() {
            this.updatePosition();
            this.isVisible = true;
        },
        hideTemporarily() {
            if (!this.isPinned) {
                this.isVisible = false;
            }
        },
        togglePinned() {
            this.isPinned = !this.isPinned;
            if (this.isPinned) {
                this.updatePosition();
            }
            this.isVisible = this.isPinned;
        },
        updatePosition() {
            if (!this.$refs.root) return;

            const trigger = this.$refs.root.getBoundingClientRect();
            this.tooltipPosition = {
                left: `${trigger.right}px`,
                top: `${trigger.top - 8}px`,
            };
        },
        close() {
            this.isPinned = false;
            this.isVisible = false;
        },
        handleOutsideClick(event) {
            if (this.isPinned && !this.$refs.root.contains(event.target)) {
                this.close();
            }
        },
        handleKeydown(event) {
            if (event.key === 'Escape') {
                this.close();
            }
        },
    },
};
</script>

<style scoped>
.info-tooltip {
    position: relative;
    display: inline-flex;
    color: var(--ca-color-text-muted, #9a9a9a);
}

.info-tooltip__trigger {
    display: inline-flex;
    align-items: center;
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    cursor: pointer;
}

.info-tooltip__trigger:hover,
.info-tooltip__trigger:focus-visible {
    color: var(--ca-color-text-primary, #666666);
}

.info-tooltip--preserve-trigger-color .info-tooltip__trigger:hover,
.info-tooltip--preserve-trigger-color .info-tooltip__trigger:focus-visible {
    color: inherit;
}

.info-tooltip--preserve-trigger-color .info-tooltip__trigger:hover > .fa,
.info-tooltip--preserve-trigger-color
    .info-tooltip__trigger:focus-visible
    > .fa,
.info-tooltip--preserve-trigger-color
    .info-tooltip__trigger[aria-expanded='true']
    > .fa {
    color: var(--ca-color-text-primary, #666666);
}

.info-tooltip__trigger:focus-visible {
    outline: 2px solid var(--ca-color-primary, #7a9e9f);
    outline-offset: 2px;
    border-radius: 2px;
}

.info-tooltip__content {
    position: fixed;
    z-index: 10000;
    width: max-content;
    max-width: min(280px, 80vw);
    padding: 8px 10px;
    border: 1px solid var(--ca-chart-tooltip-border, rgba(255, 255, 255, 0.12));
    border-radius: 4px;
    background: var(--ca-chart-tooltip-bg, rgba(31, 35, 41, 0.96));
    box-shadow: 0 4px 12px rgba(31, 35, 41, 0.18);
    color: var(--ca-chart-tooltip-text, #f3f4f6);
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 1.4;
    text-align: left;
    white-space: normal;
    transform: translate(-100%, -100%);
    pointer-events: none;
}
</style>
